import { Mol, Reaction as ReactionType } from '@moleculeone/types';
import { MouseEventHandler, RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { BaseReactionSVG, ReagentClassNameMapper, SubstrateMouseEventHandler } from './BaseReactionSVG';
import { ReagentCompoundRenderer } from './Reagent';
import { ArrowProps } from './Signs';
import { alignSubstratesToProduct } from './utils/alignSubstratesToProduct';
import { HoverState } from './utils/buildHoverState';
import { ORIENTATION_LEFT, REACTION_SIZE_REGULAR } from './utils/const';
import { drawMols } from './utils/drawMols';
import { getInputStream } from './utils/getInputStream';
import { HoverType, getMolDrawers } from './utils/getMolDrawers';
import { ReactionHighlights } from './utils/getPlainMolDrawers';
import { reverseMapping } from './utils/reverseMapping';

export interface BaseReactionProps {
  arrowProps?: ArrowProps;
  className?: string;
  highlights?: ReactionHighlights;
  interactive?: boolean;
  onProductClick?: MouseEventHandler;
  onReady?: () => void;
  onSubstrateClick?: SubstrateMouseEventHandler;
  orientation?: string;
  productMap: ReactionType['productMap'];
  productMol: Mol;
  reagentClassName?: string | ReagentClassNameMapper;
  renderCompound?: ReagentCompoundRenderer;
  showDisconnections?: boolean;
  showSubstrates?: boolean;
  shouldAlignSubstrates?: boolean;
  size?: number;
  substrateMols: Mol[];
  substratesMap: ReactionType['substratesMap'];
}

export function BaseReaction(props: BaseReactionProps) {
  const {
    arrowProps = {},
    className,
    highlights = [],
    interactive = false,
    onProductClick,
    onReady = () => {},
    onSubstrateClick,
    orientation = ORIENTATION_LEFT,
    productMap,
    productMol,
    reagentClassName,
    renderCompound,
    showDisconnections = false,
    showSubstrates = true,
    shouldAlignSubstrates = true,
    size = REACTION_SIZE_REGULAR,
    substrateMols,
    substratesMap,
  } = props;

  const [hoverState, setHoverState] = useState<HoverState>({
    type: HoverType.HOVER_NONE,
    substrateIndex: null,
    mapId: null,
  });

  const { mapToProduct, mapToSubstrates } = useMemo(
    () => reverseMapping(productMap, substratesMap),
    [productMap, substratesMap],
  );

  const alignedSubstrateMols = useMemo(() => {
    if (!shouldAlignSubstrates) {
      return substrateMols;
    }

    return alignSubstratesToProduct(productMol, substrateMols, substratesMap, mapToProduct);
  }, [productMol, substrateMols, substratesMap, shouldAlignSubstrates, mapToProduct]);

  const { drawProduct, drawSubstrate } = useMemo(() => {
    return getMolDrawers(
      productMap,
      substratesMap,
      highlights,
      mapToSubstrates,
      hoverState.type as string,
      hoverState.substrateIndex as number,
    );
  }, [productMap, substratesMap, highlights, mapToSubstrates, hoverState.type, hoverState.substrateIndex]);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const inputStream = getInputStream(
      { productMol, productMap, substratesMap },
      {
        ref: ref as RefObject<HTMLDivElement>,
        alignedSubstrateMols,
        mapToProduct,
        mapToSubstrates,
      },
    );
    const inputSubscription = inputStream.subscribe(state => {
      setHoverState(state);
    });

    const listener = (event: any): void => {
      inputStream.next(event);
    };

    document.addEventListener('mousemove', listener);
    document.addEventListener('keydown', listener);
    document.addEventListener('keyup', listener);

    return (): void => {
      document.removeEventListener('mousemove', listener);
      document.removeEventListener('keydown', listener);
      document.removeEventListener('keyup', listener);
      inputSubscription.unsubscribe();
    };
  }, [productMol, alignedSubstrateMols, productMap, substratesMap, ref, mapToProduct, mapToSubstrates]);

  useEffect(() => {
    onReady();
  }, []);

  const [drawnProductMol, ...drawnSubstrateMols] = drawMols(
    productMol,
    productMap,
    showDisconnections,
    showSubstrates,
    mapToSubstrates,
    alignedSubstrateMols,
    drawProduct,
    drawSubstrate,
  );

  return (
    <BaseReactionSVG
      arrowProps={arrowProps}
      className={className}
      drawnProductMol={drawnProductMol}
      drawnSubstrateMols={drawnSubstrateMols}
      interactive={interactive}
      onProductClick={onProductClick}
      onSubstrateClick={onSubstrateClick}
      options={{
        ref,
        alignedSubstrateMols,
        mapToProduct,
        mapToSubstrates,
        hoverState,
      }}
      orientation={orientation}
      productMol={productMol}
      reagentClassName={reagentClassName}
      renderCompound={renderCompound}
      size={size}
      showSubstrates={showSubstrates}
    />
  );
}
